import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getAccessToken, getRefreshToken, setTokens, clearTokens, getTrustedDevice, setTrustedDevice, getActiveTenant } from '@/lib/authToken';
import { installRefreshInterceptor } from '@/lib/authRefresh';
import { signOutForReason } from '@/lib/loginNotice';
import { setActiveCompanyTz } from '@/lib/companyTime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// iter-156sf — attach the stored Bearer access token to every request so
// Safari (which blocks the cross-site auth cookie via ITP) stays logged
// in. Chrome keeps using the HttpOnly cookie; the header is redundant
// there. Skipped if some caller already set Authorization explicitly.
axios.interceptors.request.use((config) => {
  const t = getAccessToken();
  if (!t) return config;
  // Only attach to our own backend — never leak the JWT to an external
  // host (e.g. an R2/S3 presigned URL) reached via the global instance.
  const url = config.url || '';
  const isBackend = url.startsWith('/') || url.startsWith(process.env.REACT_APP_BACKEND_URL || '\u0000');
  if (isBackend) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
    const tenant = getActiveTenant();
    if (tenant && !config.headers['X-Active-Tenant']) config.headers['X-Active-Tenant'] = tenant;
  }
  return config;
});

// Auto-refresh token on 401 with a single-flight lock. The interceptor
// logic now lives in `lib/authRefresh.js` so the SAME lock is shared
// with the centralized `apiClient` instance (report form + photo
// upload), which previously had no refresh interceptor and bounced
// field techs to login when their 60-min access token expired
// mid-walk. See that module for the full concurrency model.
installRefreshInterceptor(axios);

// NOTE: The dev-mode error-overlay silencer used to live here, but
// it has been hoisted to `src/index.js` so it runs BEFORE any other
// module evaluates (including this one). That guarantees the
// overlay's runtime-error listeners are detached before any axios
// request can fire and reject. See index.js for the rationale.

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (meErr) {
      // The access token may have expired while the 7-day refresh token
      // is still valid (e.g. a page reload after a deploy / tab restore /
      // crash). `/auth/me` is excluded from the global refresh
      // interceptor to avoid loops, so attempt ONE silent refresh here
      // before giving up — this keeps an active user logged in across
      // reloads instead of bouncing them to /login.
      try {
        const rt = getRefreshToken();
        const { data: rd } = await axios.post(
          `${API}/auth/refresh`, {},
          { withCredentials: true, headers: rt ? { 'X-Refresh-Token': rt } : {} },
        );
        setTokens(rd);
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
      } catch (refreshErr) {
        // signed out with a reason (shown on the login page; session ended so a reload doesn't repeat it)
        if (!(await signOutForReason(refreshErr))) await signOutForReason(meErr);
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Keep the app-wide "active company timezone" in sync so every pure
  // date formatter (leadConstants, files helpers, lib/tz, etc.) renders
  // in the company zone rather than the viewer's device zone.
  useEffect(() => {
    if (user && user.company_timezone) setActiveCompanyTz(user.company_timezone);
  }, [user]);

  // After a session is established (login / MFA / register), the auth
  // response is a token-shaped payload that omits some /auth/me-only
  // fields (company feature flags like metric_library_enabled,
  // sms_ui_enabled, effective permissions, etc.). Hydrate the full user
  // from /auth/me so those surfaces render immediately without a reload.
  const hydrateUser = async (fallback) => {
    try {
      const { data: me } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(me);
      return me;
    } catch {
      setUser(fallback);
      return fallback;
    }
  };

  const login = async (email, password, turnstile_token) => {
    // The trusted-device token rides as a header because the cookie version is
    // third-party on a custom domain and gets dropped by the browser — that is
    // why "Remember this device for 30 days" appeared to do nothing.
    const td = getTrustedDevice();
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password, turnstile_token },
      { withCredentials: true, headers: td ? { 'X-Trusted-Device': td } : undefined },
    );
    // Bot challenge: server asks for Turnstile verification (suspicious login).
    // Do NOT establish a session — LoginPage shows the widget and retries.
    if (data && data.captcha_required) {
      return data; // { captcha_required: true, message }
    }
    // Two-factor gate: when the server returns a challenge, do NOT
    // establish a session — the LoginPage collects a 2FA code and calls
    // verifyMfa() to complete sign-in.
    if (data && data.mfa_required) {
      return data; // { mfa_required: true, challenge_token }
    }
    setTokens(data);
    await hydrateUser(data);
    return data;
  };

  const verifyMfa = async (challenge_token, code, remember_device = false) => {
    const { data } = await axios.post(`${API}/mfa/verify`, { challenge_token, code, remember_device }, { withCredentials: true });
    if (data?.trusted_device_token) setTrustedDevice(data.trusted_device_token);
    setTokens(data);
    await hydrateUser(data);
    return data;
  };

  const register = async (email, password, name, company_name, license_key) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name, company_name, license_key }, { withCredentials: true });
    setTokens(data);
    await hydrateUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      clearTokens();
      setUser(false);
    }
  };

  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyMfa, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}


// For components that also render outside the provider (isolated tests): null instead of throwing.
export function useOptionalAuth() {
  return useContext(AuthContext);
}
